/**
 * Member Card Portal Component
 * Member-facing view of their prepaid card with transaction history
 * Trust Marketplace - Phase 3: Payment Infrastructure (Stripe Issuing)
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import { getMemberToken } from '@/lib/memberAuth';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  CreditCard,
  Eye,
  EyeOff,
  Lock,
  DollarSign,
  ShoppingBag,
  TrendingUp,
  Calendar,
  CheckCircle,
  AlertCircle,
  MapPin,
  RefreshCw,
  Download
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface MemberCardPortalProps {
  memberId: number;
}

interface MemberCard {
  id: number;
  card_number: string;
  card_number_last4: string;
  card_type: string;
  card_status: string;
  spending_limit_amount: number;
  spending_limit_interval: string;
  current_period_spending: number;
  remaining_balance: number;
  allowed_categories?: string[];
  blocked_categories?: string[];
  issued_date: string;
  expiration_date: string;
  expiration_month: string;
  expiration_year: string;
}

interface RecentTransaction {
  id: number;
  transaction_number: string;
  amount: number;
  merchant_name?: string;
  merchant_category: string;
  transaction_date: string;
  authorization_status: string;
  settlement_status: string;
}

const MemberCardPortal: React.FC<MemberCardPortalProps> = ({ memberId }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [card, setCard] = useState<MemberCard | null>(null);
  const [transactions, setTransactions] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFullCard, setShowFullCard] = useState(false);

  useEffect(() => {
    fetchCardData();
  }, [memberId]);

  const fetchCardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use member token instead of auth token
      const token = getMemberToken();

      if (!token) {
        // Check if user is logged in as organization/staff instead
        const orgToken = localStorage.getItem('auth_token');
        if (orgToken) {
          setError('This page is for members only. Organization users should use Card Management from the admin panel.');
        } else {
          setError('Not authenticated. Please log in as a member to view your card.');
        }
        setLoading(false);
        return;
      }

      // Fetch card details and recent transactions in parallel — with the
      // MEMBER token (authFetch would send the staff auth_token → 401 for a
      // member session).
      const memberHeaders = {
        Authorization: `Bearer ${token}`,
        'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
      };
      const [cardRes, transactionsRes] = await Promise.all([
        fetch(getWeaveinApiUrl(`/api/cards/member/${memberId}`), { headers: memberHeaders }),
        fetch(getWeaveinApiUrl(`/api/card-transactions/member/${memberId}?limit=10`), { headers: memberHeaders })
      ]);

      if (!cardRes.ok) {
        if (cardRes.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        // No card / no card program for this member (404, or a 4xx/5xx when the
        // org has no card program — e.g. a CBO-only member). This is not an
        // error to the member; show the "no card" state instead of a red error.
        setCard(null);
        setTransactions([]);
        setLoading(false);
        return;
      }

      if (!transactionsRes.ok && transactionsRes.status !== 404) {
        console.warn('Failed to fetch transactions, but continuing with card data');
      }

      const [cardData, transactionsData] = await Promise.all([
        cardRes.json(),
        transactionsRes.ok ? transactionsRes.json() : Promise.resolve([])
      ]);

      // API returns array, get first card
      setCard(Array.isArray(cardData) ? cardData[0] : cardData);
      setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
    } catch (err) {
      console.error('Error fetching card data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const requestPhysicalCard = async () => {
    if (!card) return;

    if (!confirm('Request a physical card? Your virtual card will remain active.')) {
      return;
    }

    try {
      const url = getWeaveinApiUrl('/api/cards/request-physical');
      const token = getMemberToken();

      if (!token) {
        alert('Session expired. Please log in again.');
        return;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ member_id: memberId }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error('Failed to request physical card');
      }

      alert('Physical card requested! You will receive it within 7-10 business days.');
      fetchCardData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to request physical card');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.medium(dateString);
  };

  const formatDateTime = (dateString: string) => {
    return fmt.dateTime(dateString);
  };

  const calculateSpendingPercentage = (): number => {
    if (!card || card.spending_limit_amount === 0) return 0;
    return Math.round((card.current_period_spending / card.spending_limit_amount) * 100);
  };

  const getStatusColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
      case 'SETTLED':
        return 'text-green-600';
      case 'PENDING':
        return 'text-yellow-600';
      case 'DECLINED':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
        <Button variant="outline" onClick={fetchCardData}>
          {t('payments.try_again', 'Try Again')}
        </Button>
      </Card>
    );
  }

  if (!card) {
    return (
      <Card className="p-12 text-center">
        <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('payments.no_card_available', 'No card available')}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t('payments.you_dont_have_an_active', 'You don\'t have an active prepaid card yet. Contact your administrator to get started.')}
        </p>
      </Card>
    );
  }

  const spendingPercentage = calculateSpendingPercentage();
  const isNearLimit = spendingPercentage >= 80;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('payments.my_card', 'My Card')}</h2>}
          <p className="text-sm text-gray-600 mt-1">{t('payments.manage_your_prepaid_card_and', 'Manage your prepaid card and view transactions')}</p>
        </div>
        <Button variant="outline" onClick={fetchCardData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('payments.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Virtual Card Display */}
      <Card className="p-8 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>

        <div className="relative z-10">
          {/* Card Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="text-xs opacity-75 mb-1">{t('payments.weavein_prepaid_card', 'WeaveIn Prepaid Card')}</div>
              <Badge className="bg-white/20 text-white border-white/30">
                {card.card_type}
              </Badge>
            </div>
            <CreditCard className="w-10 h-10 opacity-75" />
          </div>

          {/* Card Number */}
          <div className="mb-6">
            <div className="flex items-center gap-4 mb-2">
              <div className="font-mono text-2xl tracking-wider">
                {showFullCard ? card.card_number : `•••• •••• •••• ${card.card_number_last4}`}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20"
                onClick={() => setShowFullCard(!showFullCard)}
              >
                {showFullCard ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Card Details */}
          <div className="flex items-end justify-between">
            <div>
              <div className="text-xs opacity-75 mb-1">{t('payments.valid_thru', 'Valid Thru')}</div>
              <div className="font-mono text-lg">
                {card.expiration_month}/{card.expiration_year}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs opacity-75 mb-1">{t('payments.status', 'Status')}</div>
              <Badge className="bg-green-500 text-white border-0">
                {card.card_status}
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* Balance and Spending */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-600 text-white">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-green-700">{t('payments.available_balance', 'Available Balance')}</div>
              <div className="text-2xl font-bold text-green-900">
                {formatCurrency(card.remaining_balance)}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white">
              <ShoppingBag className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-blue-700">Spent This {card.spending_limit_interval}</div>
              <div className="text-2xl font-bold text-blue-900">
                {formatCurrency(card.current_period_spending)}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-600 text-white">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-purple-700">{t('payments.spending_limit', 'Spending Limit')}</div>
              <div className="text-2xl font-bold text-purple-900">
                {formatCurrency(card.spending_limit_amount)}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Spending Progress */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Spending Progress ({card.spending_limit_interval})
          </h3>
          <span className={`text-sm font-semibold ${isNearLimit ? 'text-red-600' : 'text-gray-900'}`}>
            {spendingPercentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
          <div
            className={`h-3 rounded-full transition-all ${
              isNearLimit ? 'bg-red-500' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(spendingPercentage, 100)}%` }}
          ></div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-300">
            {formatCurrency(card.current_period_spending)} of {formatCurrency(card.spending_limit_amount)}
          </span>
          <span className={isNearLimit ? 'text-red-600 font-medium' : 'text-gray-600'}>
            {formatCurrency(card.spending_limit_amount - card.current_period_spending)} remaining
          </span>
        </div>
      </Card>

      {/* Allowed Categories */}
      {card.allowed_categories && card.allowed_categories.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            {t('payments.allowed_spending_categories', 'Allowed Spending Categories')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {card.allowed_categories.map(category => (
              <Badge key={category} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {category.replace('_', ' ')}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Card Actions */}
      {card.card_type === 'VIRTUAL' && (
        <Card className="p-5 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 flex-shrink-0">
              <CreditCard className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">{t('payments.want_a_physical_card', 'Want a Physical Card?')}</h4>
              <p className="text-sm text-gray-600 mb-3">
                {t('payments.request_a_physical_card_to', 'Request a physical card to use at any store. Your virtual card will remain active.')}
              </p>
              <Button onClick={requestPhysicalCard} className="bg-v2accent hover:bg-v2accent/90">
                {t('payments.request_physical_card', 'Request Physical Card')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">{t('payments.recent_transactions', 'Recent Transactions')}</h3>
          {transactions.length > 0 && (
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              {t('payments.export', 'Export')}
            </Button>
          )}
        </div>

        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('payments.no_transactions_yet', 'No transactions yet')}</p>
            <p className="text-xs text-gray-500 mt-1">{t('payments.your_purchases_will_appear_here', 'Your purchases will appear here')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map(transaction => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex-shrink-0">
                    <ShoppingBag className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {transaction.merchant_name || 'Purchase'}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Badge variant="outline" className="text-xs">
                        {transaction.merchant_category}
                      </Badge>
                      <span>•</span>
                      <span>{formatDateTime(transaction.transaction_date)}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(transaction.amount)}
                  </div>
                  <div className={`text-xs ${getStatusColor(transaction.settlement_status)}`}>
                    {transaction.settlement_status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Security Notice */}
      <Card className="p-5 bg-yellow-50 border-yellow-200">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">{t('payments.security_notice', 'Security Notice')}</h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Never share your card number, expiration date, or CVV with anyone. WeaveIn will never ask for this
              information via email or phone. Report suspicious activity immediately.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MemberCardPortal;
